import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { useTranslation } from "../../../utils/translation";
import {
  addBeetinqTeamMember,
  listBeetinqTeamMembers,
  resetBeetinqTeamMessages,
  setBeetinqTeamMemberStatus,
  updateBeetinqTeamMember,
} from "../beetinqTeamSlice";
import {
  selectBeetinqTeamCreateError,
  selectBeetinqTeamCreateLoading,
  selectBeetinqTeamCreateMessage,
  selectBeetinqTeamError,
  selectBeetinqTeamLoading,
  selectBeetinqTeamMembers,
  selectBeetinqTeamMessage,
  selectBeetinqTeamUpdateError,
  selectBeetinqTeamUpdateLoading,
  selectBeetinqTeamUpdateMessage,
} from "../beetinqTeamSelectors";
import { BEETINQ_TEAM_PAGE_SIZE } from "../beetinqTeam.types";

const formatDate = (value) => {
  if (!value) return "-";
  if (typeof value === "number") return new Date(value).toLocaleString("tr-TR");
  if (typeof value?.toDate === "function") return value.toDate().toLocaleString("tr-TR");
  return "-";
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const ROW_LIGHT_BLUE = "#eef7ff";
const ROW_POWDER_PINK = "#f6f6f6ff";

const highlightMatch = (text, term) => {
  if (!term) return text;
  const safeText = text || "";
  const lower = safeText.toLowerCase();
  const lowerTerm = term.toLowerCase();
  const index = lower.indexOf(lowerTerm);
  if (index === -1) return safeText || "-";
  const before = safeText.slice(0, index);
  const match = safeText.slice(index, index + term.length);
  const after = safeText.slice(index + term.length);
  return (
    <>
      {before}
      <span style={{ backgroundColor: "yellow" }}>{match}</span>
      {after}
    </>
  );
};

const BeetinqTeamManagementPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const members = useSelector(selectBeetinqTeamMembers);
  const isLoading = useSelector(selectBeetinqTeamLoading);
  const isError = useSelector(selectBeetinqTeamError);
  const message = useSelector(selectBeetinqTeamMessage);
  const createLoading = useSelector(selectBeetinqTeamCreateLoading);
  const createError = useSelector(selectBeetinqTeamCreateError);
  const createMessage = useSelector(selectBeetinqTeamCreateMessage);
  const updateLoading = useSelector(selectBeetinqTeamUpdateLoading);
  const updateError = useSelector(selectBeetinqTeamUpdateError);
  const updateMessage = useSelector(selectBeetinqTeamUpdateMessage);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [removeTargetId, setRemoveTargetId] = useState("");
  const [reactivateTargetId, setReactivateTargetId] = useState("");
  const [editTargetId, setEditTargetId] = useState("");
  const [localSuccess, setLocalSuccess] = useState("");
  const [addForm, setAddForm] = useState({
    name: "",
    position: "",
    photo: "",
    order: "0",
    userId: "",
    startDate: "",
    endDate: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    position: "",
    photo: "",
    order: "0",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    dispatch(listBeetinqTeamMembers());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetBeetinqTeamMessages());
    };
  }, [dispatch]);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members || [];
    return (members || []).filter((member) => String(member?.name || "").toLowerCase().includes(term));
  }, [members, search]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / BEETINQ_TEAM_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedMembers = useMemo(() => {
    const start = (currentPage - 1) * BEETINQ_TEAM_PAGE_SIZE;
    return filteredMembers.slice(start, start + BEETINQ_TEAM_PAGE_SIZE);
  }, [filteredMembers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const activeMembers = useMemo(() => {
    return (members || []).filter((member) => Boolean(member?.status));
  }, [members]);

  const passiveMembers = useMemo(() => {
    return (members || []).filter((member) => !member?.status);
  }, [members]);

  const onAddFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setAddForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setReactivateTargetId("");
    setAddForm({
      name: "",
      position: "",
      photo: "",
      order: "0",
      userId: "",
      startDate: "",
      endDate: "",
    });
  };

  const handleAddMember = async () => {
    setLocalSuccess("");
    if (reactivateTargetId) {
      const target = passiveMembers.find((member) => member.id === reactivateTargetId);
      try {
        await dispatch(
          setBeetinqTeamMemberStatus({
            id: reactivateTargetId,
            status: true,
          })
        ).unwrap();
        setLocalSuccess(t("admin_beetinq_team.reactivate_success", { name: target?.name || "" }));
        closeAddModal();
      } catch (error) {
        // handled by slice
      }
      return;
    }

    const trimmedName = addForm.name.trim();
    if (!trimmedName) return;

    try {
      await dispatch(
        addBeetinqTeamMember({
          name: trimmedName,
          position: addForm.position,
          photo: addForm.photo,
          order: addForm.order,
          userId: addForm.userId,
          startDate: addForm.startDate || null,
          endDate: addForm.endDate || null,
        })
      ).unwrap();
      setLocalSuccess(t("admin_beetinq_team.add_success", { name: trimmedName }));
      closeAddModal();
    } catch (error) {
      // handled by slice
    }
  };

  const openRemoveModal = () => {
    const firstActiveId = activeMembers[0]?.id || "";
    setRemoveTargetId(firstActiveId);
    setShowRemoveModal(true);
  };

  const closeRemoveModal = () => {
    setShowRemoveModal(false);
    setRemoveTargetId("");
  };

  const openEditModal = (member) => {
    setEditTargetId(member?.id || "");
    setEditForm({
      name: member?.name || "",
      position: member?.position || "",
      photo: member?.photo || "",
      order: String(member?.order ?? 0),
      startDate: toDateTimeLocalValue(member?.startDate),
      endDate: toDateTimeLocalValue(member?.endDate),
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditTargetId("");
    setEditForm({
      name: "",
      position: "",
      photo: "",
      order: "0",
      startDate: "",
      endDate: "",
    });
  };

  const handleRemoveMember = async () => {
    if (!removeTargetId) return;
    const target = activeMembers.find((member) => member.id === removeTargetId);
    try {
      await dispatch(
        setBeetinqTeamMemberStatus({
          id: removeTargetId,
          status: false,
        })
      ).unwrap();
      setLocalSuccess(t("admin_beetinq_team.remove_success", { name: target?.name || "" }));
      closeRemoveModal();
    } catch (error) {
      // handled by slice
    }
  };

  const handleEditMember = async () => {
    if (!editTargetId || !editForm.name.trim()) return;
    try {
      await dispatch(
        updateBeetinqTeamMember({
          id: editTargetId,
          name: editForm.name,
          position: editForm.position,
          photo: editForm.photo,
          order: editForm.order,
          startDate: editForm.startDate || null,
          endDate: editForm.endDate || null,
        })
      ).unwrap();
      setLocalSuccess(t("admin_beetinq_team.edit_success", { name: editForm.name.trim() }));
      closeEditModal();
    } catch (error) {
      // handled by slice
    }
  };

  const renderRows = () => {
    if (isLoading && (!members || members.length === 0)) {
      return (
        <tr>
          <td colSpan="9">{t("admin_common.loading")}</td>
        </tr>
      );
    }

    if (isError && (!members || members.length === 0)) {
      return (
        <tr>
          <td colSpan="9" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }

    if (!pagedMembers || pagedMembers.length === 0) {
      return (
        <tr>
          <td colSpan="9">{t("admin_common.no_records")}</td>
        </tr>
      );
    }

    return pagedMembers.map((member, index) => {
      const isActive = Boolean(member?.status);
      const rowStyle = { backgroundColor: index % 2 === 0 ? ROW_LIGHT_BLUE : ROW_POWDER_PINK };
      return (
        <tr key={member.id} style={rowStyle}>
          <td>
            {member?.photo ? (
              <img
                src={member.photo}
                alt={member?.name || "member"}
                style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "50%" }}
              />
            ) : (
              <span className="badge badge-light">-</span>
            )}
          </td>
          <td>{highlightMatch(member?.name || "-", search.trim())}</td>
          <td>{member?.position || "-"}</td>
          <td>{member?.order ?? "-"}</td>
          <td>{formatDate(member?.startDate)}</td>
          <td>{formatDate(member?.endDate)}</td>
          <td>{member?.state ? t("admin_common.status_active") : t("admin_common.status_passive")}</td>
          <td>
            <span className={`badge ${isActive ? "badge-success" : "badge-secondary"}`}>
              {isActive ? t("admin_beetinq_team.status_active") : t("admin_beetinq_team.status_passive")}
            </span>
          </td>
          <td>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openEditModal(member)}>
              {t("admin_common.edit")}
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h4 className="card-title mb-0">{t("admin_beetinq_team.title")}</h4>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-success" onClick={() => setShowAddModal(true)}>
                    {t("admin_beetinq_team.add_member")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={openRemoveModal}
                    disabled={activeMembers.length === 0}
                  >
                    {t("admin_beetinq_team.remove_member")}
                  </button>
                </div>
              </div>

              {localSuccess && <div className="alert alert-success">{localSuccess}</div>}
              {(createError || updateError) && (
                <div className="alert alert-danger">
                  {createMessage || updateMessage || t("admin_common.error_generic")}
                </div>
              )}

              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>{t("admin_beetinq_team.photo")}</th>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_beetinq_team.position")}</th>
                      <th>{t("admin_beetinq_team.order")}</th>
                      <th>{t("admin_beetinq_team.start_date")}</th>
                      <th>{t("admin_beetinq_team.end_date")}</th>
                      <th>{t("admin_beetinq_team.state")}</th>
                      <th>{t("admin_beetinq_team.status")}</th>
                      <th>{t("admin_common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">{t("admin_common.total_records", { count: filteredMembers.length })}</span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal className="modal fade" show={showAddModal} onHide={closeAddModal}>
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title fs-20">{t("admin_beetinq_team.add_member")}</h4>
            <button type="button" className="btn-close" onClick={closeAddModal}>
              <span></span>
            </button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_beetinq_team.reactivate_member")}</label>
              <select
                className="form-control"
                value={reactivateTargetId}
                onChange={(e) => setReactivateTargetId(e.target.value)}
              >
                <option value="">{t("admin_beetinq_team.new_member_option")}</option>
                {passiveMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member?.name || "-"} - {member?.position || "-"}
                  </option>
                ))}
              </select>
            </div>
            {reactivateTargetId ? (
              <div className="alert alert-info mb-0">{t("admin_beetinq_team.reactivate_hint")}</div>
            ) : (
              <>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_common.name")}</label>
              <input name="name" className="form-control" value={addForm.name} onChange={onAddFormChange} />
            </div>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_beetinq_team.position")}</label>
              <input name="position" className="form-control" value={addForm.position} onChange={onAddFormChange} />
            </div>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_beetinq_team.photo_url")}</label>
              <input name="photo" className="form-control" value={addForm.photo} onChange={onAddFormChange} />
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="text-black font-w500">{t("admin_beetinq_team.order")}</label>
                <input name="order" type="number" className="form-control" value={addForm.order} onChange={onAddFormChange} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="text-black font-w500">{t("admin_common.user_id")}</label>
                <input name="userId" className="form-control" value={addForm.userId} onChange={onAddFormChange} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="text-black font-w500">{t("admin_beetinq_team.start_date")}</label>
                <input
                  name="startDate"
                  type="datetime-local"
                  className="form-control"
                  value={addForm.startDate}
                  onChange={onAddFormChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="text-black font-w500">{t("admin_beetinq_team.end_date")}</label>
                <input
                  name="endDate"
                  type="datetime-local"
                  className="form-control"
                  value={addForm.endDate}
                  onChange={onAddFormChange}
                />
              </div>
            </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={closeAddModal}>
              {t("admin_common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleAddMember}
              disabled={createLoading || updateLoading || (!reactivateTargetId && !addForm.name.trim())}
            >
              {createLoading ? t("admin_common.loading") : t("admin_common.save")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal className="modal fade" show={showRemoveModal} onHide={closeRemoveModal}>
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title fs-20">{t("admin_beetinq_team.remove_member")}</h4>
            <button type="button" className="btn-close" onClick={closeRemoveModal}>
              <span></span>
            </button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_beetinq_team.select_member")}</label>
              <select
                className="form-control"
                value={removeTargetId}
                onChange={(e) => setRemoveTargetId(e.target.value)}
              >
                {activeMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member?.name || "-"} - {member?.position || "-"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={closeRemoveModal}>
              {t("admin_common.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={handleRemoveMember} disabled={updateLoading || !removeTargetId}>
              {updateLoading ? t("admin_common.loading") : t("admin_common.delete")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal className="modal fade" show={showEditModal} onHide={closeEditModal}>
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title fs-20">{t("admin_common.edit")}</h4>
            <button type="button" className="btn-close" onClick={closeEditModal}>
              <span></span>
            </button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_common.name")}</label>
              <input name="name" className="form-control" value={editForm.name} onChange={onEditFormChange} />
            </div>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_beetinq_team.position")}</label>
              <input name="position" className="form-control" value={editForm.position} onChange={onEditFormChange} />
            </div>
            <div className="mb-3">
              <label className="text-black font-w500">{t("admin_beetinq_team.photo_url")}</label>
              <input name="photo" className="form-control" value={editForm.photo} onChange={onEditFormChange} />
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="text-black font-w500">{t("admin_beetinq_team.order")}</label>
                <input
                  name="order"
                  type="number"
                  className="form-control"
                  value={editForm.order}
                  onChange={onEditFormChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="text-black font-w500">{t("admin_beetinq_team.start_date")}</label>
                <input
                  name="startDate"
                  type="datetime-local"
                  className="form-control"
                  value={editForm.startDate}
                  onChange={onEditFormChange}
                />
              </div>
            </div>
            <div className="mb-0">
              <label className="text-black font-w500">{t("admin_beetinq_team.end_date")}</label>
              <input
                name="endDate"
                type="datetime-local"
                className="form-control"
                value={editForm.endDate}
                onChange={onEditFormChange}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={closeEditModal}>
              {t("admin_common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEditMember}
              disabled={updateLoading || !editForm.name.trim()}
            >
              {updateLoading ? t("admin_common.loading") : t("admin_common.save")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BeetinqTeamManagementPage;
